import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import getVideoId from 'get-video-id'
import type { FormikProps } from 'formik'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ModalVideo from 'react-modal-video'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import {
  useGetDevelopmentQuery,
  useUpdateDevelopmentMutation,
  getUser,
  developmentsApi,
} from '@homeviews/store'
import {
  Typography,
  Button,
  TextField,
  Card,
  Select,
  StatusLabel,
  Spinner,
  Snackbar,
  LockedContentHeader,
  InfoBanner,
  ActivityLog,
} from '@homeviews/shared-ui'
import type { ITheme } from '@homeviews/styles'
import {
  DevelopmentType,
  FACILITIES,
  FacilityOption,
} from '@homeviews/developments'
import {
  Draggable,
  formatToFullDate,
  stripHtml,
  getFormChanges,
} from '@homeviews/utils'
import { HorizontalCard, MediaCore } from '@homeviews/media'
import { HAS_PERMISSION } from '@homeviews/users'
import { mixpanelTrack } from '@homeviews/seo'

import 'react-modal-video/scss/modal-video.scss'
import type { MediaItem } from '@homeviews/models'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      position: 'relative',
    },
    columns: {
      flexWrap: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
      },
    },
    pageTitle: {},
    mainColumn: {},
    sideColumn: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    card: {
      width: '100%',
    },
    cardWrapper: {
      width: '100%',
    },
    sidebarInner: {
      width: 317,
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        width: 'initial',
      },
    },
    sidebarButton: {
      marginTop: theme.spacing(1.875),
    },
    summaryText: {
      color: theme.colors.grey490,
      marginTop: theme.spacing(1.875),
    },
    editMeta: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    editMetaLabel: {
      flex: ' 0 0 50%',
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(0.625),
      fontWeight: 500,
      color: theme.colors.grey450,
    },
    editMetaValue: {
      flex: ' 0 0 50%',
      marginBottom: theme.spacing(0.625),
      color: theme.colors.grey800,
    },
    statusHeader: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  }),
)

interface IDevelopmentEditForm {
  id: number
  content: string
  video_url: string
  images: any[]
  facilities: any[]
  facility_suggestion: string
  updated_at: string
}

interface IVideoPreview {
  videoId: string | number
  videoService: string
}

const emptyFormState = {
  id: null,
  content: '',
  video_url: '',
  images: [],
  facilities: [],
  facility_suggestion: '',
  updated_at: '',
}

const DevelopmentEditForm: FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [updateDevelopment, { isLoading }] = useUpdateDevelopmentMutation()
  const classes = useStyles()
  const { id: developmentID } = useParams<{ id: string }>()
  const user = useSelector(getUser)

  const [initialFormState, setInitialFormState] =
    useState<IDevelopmentEditForm>(emptyFormState)
  const [loading, setLoading] = useState<boolean>(false)
  const [processing, setProcessing] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [formSuccessMsg, setFormSuccessMsg] = useState<string>('')
  const [videoPreview, setVideoPreview] = useState<IVideoPreview>(null)
  const [mediaLibraryOpened, setMediaLibraryOpened] = useState<boolean>(false)
  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message: string
    status?: 'success' | 'error'
  }>({
    open: false,
    message: '',
    status: undefined,
  })

  const {
    data: initialData,
    error,
    isFetching,
    isLoading: isLoadingInitialData,
  } = useGetDevelopmentQuery(developmentID)

  useEffect(() => {
    const fetchError: any = error
    if (fetchError?.status) {
      setProcessing(false)
      setLoading(false)

      setSnackbarVisible({
        open: true,
        message:
          fetchError?.status === 401 || fetchError?.status === 403
            ? 'You do not have permission to edit this development.'
            : 'Oops, something went wrong. You might not have permission to edit this development',
        status: 'error',
      })
    }
  }, [error])

  useEffect(() => {
    if (
      initialFormState?.updated_at &&
      initialFormState?.updated_at !== initialData?.updated_at
    ) {
      setProcessing(false)
      setLoading(false)
      setFormSuccessMsg(
        'Thank you for submitting your changes for review, this will be reviewed by the HomeViews team and you’ll be updated via email shortly.',
      )
      setSnackbarVisible({
        open: true,
        message: 'Changes requested',
      })
    }

    if (!initialFormState?.id) {
      setInitialFormState({
        id: initialData?.ID,
        content: initialData?.content || '',
        video_url: initialData?.video_url || '',
        images: initialData?.media || [],
        facilities: initialData?.facilities || [],
        facility_suggestion: initialData?.facility_suggestion || '',
        updated_at: initialData?.updated_at,
      })
    }
  }, [initialData])

  useEffect(() => {
    if (
      user &&
      !HAS_PERMISSION(user, 'EDIT_DEVELOPMENT', 'development', {
        devId: developmentID,
      })
    ) {
      history.push('/developments')
    }
  }, [user, history, developmentID])

  useEffect(() => {
    let processingInterval = null

    if (processing) {
      processingInterval = setInterval(() => {
        dispatch(
          developmentsApi.endpoints.getDevelopment.initiate(developmentID, {
            forceRefetch: true,
          }),
        )
      }, 1000)
    }

    return () => {
      if (processingInterval) {
        clearInterval(processingInterval)
      }
    }
  }, [developmentID, dispatch, processing])

  const onSubmit = async (values: IDevelopmentEditForm) => {
    setLoading(true)

    const images = values.images.map((image: MediaItem) => image?.id)

    const payload = {
      id: developmentID,
      data: {
        content: values.content,
        video_url: values.video_url,
        facilities: values.facilities,
        facility_suggestion: values.facility_suggestion,
        facility_update_only: false,
        gallery_images: images,
      },
    }

    try {
      const respData = await updateDevelopment(payload).unwrap()
      setProcessing(true)

      const labelMap = {
        content: 'Description',
        video_url: 'Video',
        facilities: 'Facilities',
        facility_suggestion: 'Facility Suggestion',
        gallery_images: 'Gallery Images',
      }
      const formChanges = getFormChanges(
        initialFormState,
        payload.data,
        labelMap,
        ['facility_update_only'],
      )
      mixpanelTrack('DEVELOPMENTS_SUBMIT_CHANGES:', {
        'Development Name': initialData?.title,
        'Fields edited': formChanges,
      })
    } catch (err) {
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
      setProcessing(false)
      setLoading(false)
    }
  }

  const handleSnackbarClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarVisible({
      open: false,
      message: snackbarVisible?.message,
      status: snackbarVisible?.status,
    })
  }

  const handleCancelClick = () => {
    mixpanelTrack('DEVELOPMENTS_CANCEL_CHANGES', {
      'Development Name': initialData?.title,
    })
    history.push('/developments')
  }

  const handleAddImagesClick = () => {
    setMediaLibraryOpened(true)
  }

  const isStandard = !DevelopmentType.isPaidProfile(initialData?.type) && !error
  const hasMediaGallery = DevelopmentType.hasMediaGallery(initialData?.type)

  const getSelectedMediaLimit = () => {
    if (initialData?.type === 'bts-brand') {
      return 1
    }
    if (hasMediaGallery) {
      return 5
    }

    return 1
  }

  if (isLoadingInitialData) {
    return <Spinner />
  }

  return (
    <div className={classes.root}>
      {isStandard && (
        <InfoBanner theme="gold" upgrade>
          <Typography className="l-infoBanner__title">
            Upgrade your account today
          </Typography>
        </InfoBanner>
      )}
      <Typography variant="h1" className={classes.pageTitle}>
        {initialData?.title || ''}
      </Typography>
      <Formik
        initialValues={initialFormState}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={Yup.object().shape({
          content: Yup.string(),
          video_url: Yup.string(),
          facility_suggestion: Yup.string(),
        })}
      >
        {(props: FormikProps<IDevelopmentEditForm>) => {
          const {
            values,
            touched,
            errors,
            handleBlur,
            handleChange,
            setFieldValue,
          } = props

          const handleFacilitiesChange = (option) => {
            const newFacilities = []
            if (!!option && !!option.length) {
              option.map(
                (option) => option.value && newFacilities.push(option.value),
              )
            }
            setFieldValue('facilities', newFacilities)
          }

          const handleVideoPreviewClick = () => {
            if (!values.video_url) return
            const { id: videoId, service: videoService } = getVideoId(
              values.video_url,
            )
            videoId &&
              videoService &&
              setVideoPreview({
                videoId,
                videoService,
              })
          }

          const handleSelectImages = (selectedImages: MediaItem[]) => {
            setMediaLibraryOpened(false)
            setFieldValue('images', selectedImages)
          }

          return (
            <Form>
              <Grid container className={classes.columns} spacing={3}>
                <Grid item className={classes.mainColumn} xs={12}>
                  <Grid container spacing={3}>
                    <Grid item className={classes.cardWrapper}>
                      <Card
                        className={classes.card}
                        header={
                          <LockedContentHeader isLocked={isStandard}>
                            <Typography
                              variant="h3"
                              className="l-lockedContent__title"
                            >
                              Image gallery
                            </Typography>
                            <Typography className="l-lockedContent__extraInfo">
                              (max {getSelectedMediaLimit()}{' '}
                              {getSelectedMediaLimit() === 1
                                ? 'image'
                                : 'images'}
                              )
                            </Typography>
                            {!error && (
                              <Button
                                variant="outlined"
                                customVariant="outlinedPrimary"
                                fullWidth
                                onClick={handleAddImagesClick}
                                size="small"
                                className="l-contentContent__actionButton"
                              >
                                Add new media
                              </Button>
                            )}
                          </LockedContentHeader>
                        }
                      >
                        <Draggable
                          list={values.images?.slice(
                            0,
                            getSelectedMediaLimit(),
                          )}
                          updateList={(newList: MediaItem[]) => {
                            setFieldValue('images', newList)
                          }}
                          DraggableChild={HorizontalCard}
                        />
                      </Card>
                    </Grid>
                    <Grid item className={classes.cardWrapper}>
                      <Card
                        className={classes.card}
                        header={
                          <Typography variant="h3">Description</Typography>
                        }
                      >
                        <TextField
                          label="Please note HomeViews will only accept factual changes to the description, this will be checked when submitted for review."
                          value={stripHtml(values.content, true)}
                          data-id="t-content"
                          id="content"
                          error={
                            errors.content && touched.content ? true : false
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={errors.content}
                          name="content"
                          fullWidth
                          multiline
                          rows={6}
                        />
                      </Card>
                    </Grid>
                    <Grid item className={classes.cardWrapper}>
                      <Card
                        className={classes.card}
                        header={
                          <LockedContentHeader
                            isLocked={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                          >
                            <Typography
                              variant="h3"
                              className="l-lockedContent__title"
                            >
                              Video
                            </Typography>
                          </LockedContentHeader>
                        }
                      >
                        <div
                          className={`l-fieldWrapper l-fieldWrapper--withButton`}
                        >
                          <TextField
                            label="Below you can set a video to show on your company page, you can add the video as a link from Vimeo or YouTube"
                            value={values.video_url}
                            data-id="t-video_url"
                            id="video_url"
                            name="video_url"
                            error={
                              errors.video_url && touched.video_url
                                ? true
                                : false
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                            locked={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                            helperText={errors.video_url}
                          />
                          <Button
                            variant="outlined"
                            customVariant="outlinedPrimary"
                            onClick={() => handleVideoPreviewClick()}
                          >
                            Preview video
                          </Button>
                        </div>
                      </Card>
                    </Grid>
                    <Grid item className={classes.cardWrapper}>
                      <Card
                        className={classes.card}
                        header={
                          <LockedContentHeader
                            isLocked={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                          >
                            <Typography
                              variant="h3"
                              className="l-lockedContent__title"
                            >
                              Facilities
                            </Typography>
                          </LockedContentHeader>
                        }
                      >
                        <div
                          className={clsx(
                            'l-fieldWrapper',
                            isStandard && 'l-fieldWrapper--locked',
                          )}
                        >
                          <Select
                            value={values.facilities}
                            options={FACILITIES}
                            components={{
                              Option: FacilityOption,
                            }}
                            onChange={handleFacilitiesChange}
                            isMulti
                            isDisabled={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                            label="Select the facilities available at this development, these will be displayed on your development page."
                          />
                        </div>
                        <div
                          className={clsx(
                            'l-fieldWrapper',
                            isStandard && 'l-fieldWrapper--locked',
                          )}
                        >
                          <TextField
                            label="Can't find a facility? suggest a new facility"
                            value={values.facility_suggestion}
                            data-id="t-facility_suggestion"
                            id="facility_suggestion"
                            name="facility_suggestion"
                            error={
                              errors.facility_suggestion &&
                              touched.facility_suggestion
                                ? true
                                : false
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                            locked={
                              isStandard || initialData?.type === 'bts-brand'
                            }
                            helperText={errors.facility_suggestion}
                          />
                        </div>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className={classes.sideColumn}>
                  <Card
                    className={clsx(classes.card, classes.sidebarInner)}
                    header={
                      <div className={classes.statusHeader}>
                        <Typography variant="h3">Publish</Typography>
                        <StatusLabel status={initialData?.status} />
                      </div>
                    }
                  >
                    {
                      // todo make sure all values are real
                    }
                    <div className={classes.editMeta}>
                      <Typography className={classes.editMetaLabel}>
                        Last updated
                      </Typography>
                      <Typography className={classes.editMetaValue}>
                        {formatToFullDate(initialData?.updated_at)}
                      </Typography>
                      {/* <Typography className={classes.editMetaLabel}>
                        Last updated by
                      </Typography>
                      <Typography className={classes.editMetaValue}>
                        Default developer
                      </Typography> */}
                    </div>
                    {formSuccessMsg ? (
                      <>
                        <Typography className={classes.summaryText}>
                          {formSuccessMsg}
                        </Typography>
                        <Button
                          variant="outlined"
                          customVariant="outlinedPrimary"
                          fullWidth
                          onClick={handleCancelClick}
                          className={classes.sidebarButton}
                        >
                          Back to developments
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography className={classes.summaryText}>
                          Submit your changes below, this will be reviewed by
                          HomeViews before publication.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          type="submit"
                          customColor="primary"
                          className={classes.sidebarButton}
                          isLoading={loading}
                        >
                          Submit changes for review
                        </Button>

                        <Button
                          variant="outlined"
                          customVariant="outlinedPrimary"
                          fullWidth
                          onClick={handleCancelClick}
                          className={classes.sidebarButton}
                        >
                          Cancel without changes
                        </Button>
                      </>
                    )}
                  </Card>
                  <ActivityLog recordType="development" />
                </Grid>
              </Grid>
              {mediaLibraryOpened && (
                <MediaCore
                  libraryType="images"
                  header="Select from your media"
                  formats="png, jpg, jpeg, svg, gif"
                  selected={values.images || []}
                  onClose={() => {
                    setMediaLibraryOpened(false)
                  }}
                  handleUseSelected={handleSelectImages}
                  openInWindow
                  selectable
                  selectLimit={getSelectedMediaLimit()}
                />
              )}
            </Form>
          )
        }}
      </Formik>

      {/* @ts-expect-error - ModalVideo types incomplete */}
      <ModalVideo
        channel={videoPreview?.videoService as any}
        isOpen={!!videoPreview?.videoId}
        videoId={videoPreview?.videoId?.toString() ?? ''}
        onClose={() => setVideoPreview(null)}
      />

      <Snackbar
        open={snackbarVisible?.open}
        onClose={handleSnackbarClose}
        alertMessage={snackbarVisible?.message}
        status={snackbarVisible?.status}
      />
    </div>
  )
}

export default DevelopmentEditForm
